import { useState } from "react";
import { Copy, Mail, MessageSquare, Check, ExternalLink, Info } from "lucide-react";
import BaseModal from "../../ui/BaseModal";
import toast from "../../../utils/toast";

/**
 * ShareClientModal - Modal for sharing client portal links.
 * Provides copy, email, SMS, and preview options.
 */
const ShareClientModal = ({ isOpen, onClose, tripId, clientName }) => {
  const [copied, setCopied] = useState(false);

  const clientPortalUrl = import.meta.env.VITE_CLIENT_PORTAL_URL || import.meta.env.VITE_CLIENT_APP_URL;
  const clientPortalLink = `${clientPortalUrl}?tripId=${tripId}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(clientPortalLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy link. Please copy manually.');
    }
  };

  const handleEmail = () => {
    const subject = encodeURIComponent(`Your Disney Itinerary from ${clientName || 'Your Agent'}`);
    const body = encodeURIComponent(
      `Hi!\n\nYour personalized Disney client portal is ready! Access it here:\n\n${clientPortalLink}\n\nHave a magical trip!`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  const handleSMS = () => {
    const message = encodeURIComponent(
      `Your Disney client portal is ready! View it here: ${clientPortalLink}`
    );
    window.open(`sms:?body=${message}`);
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title="Share Client Portal" size="sm">
      <BaseModal.Body>
        <p className="mb-4 text-sm text-gray-600">
          Share this personalized client portal with {clientName || 'your client'}:
        </p>

        {/* Link display */}
        <div className="mb-6">
          <label className="block mb-2 text-xs font-medium text-gray-700">Client Portal Link</label>
          <div className="flex gap-2 items-center">
            <input
              type="text"
              value={clientPortalLink}
              readOnly
              className="flex-1 px-3 py-2 text-sm bg-gray-50 rounded-lg border border-gray-300 focus:outline-none"
              onClick={(e) => e.target.select()}
            />
            <button
              onClick={handleCopy}
              className={`p-2 rounded-lg border transition-colors ${
                copied
                  ? 'text-white bg-green-600 border-green-600'
                  : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
              }`}
              type="button"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Sharing options */}
        <div className="space-y-2">
          <p className="mb-1 text-xs font-medium text-gray-700">Share via:</p>
          <button
            onClick={handleEmail}
            className="flex gap-2 justify-center items-center px-4 py-2.5 w-full text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-200 transition-colors hover:bg-gray-50"
            type="button"
          >
            <Mail className="w-4 h-4" /> Email Client
          </button>
          <button
            onClick={handleSMS}
            className="flex gap-2 justify-center items-center px-4 py-2.5 w-full text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-200 transition-colors hover:bg-gray-50"
            type="button"
          >
            <MessageSquare className="w-4 h-4" /> Send via SMS
          </button>
          <button
            onClick={() => window.open(clientPortalLink, '_blank')}
            className="flex gap-2 justify-center items-center px-4 py-2.5 w-full text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-200 transition-colors hover:bg-gray-50"
            type="button"
          >
            <ExternalLink className="w-4 h-4" /> Preview Portal
          </button>
        </div>

        {/* Tip */}
        <div className="flex gap-2 items-start p-3 mt-5 rounded-lg border border-gray-200 bg-gray-50">
          <Info className="flex-shrink-0 mt-0.5 w-4 h-4 text-gray-400" />
          <p className="text-xs text-gray-600">
            <span className="font-medium">Tip:</span> Clients can access their itinerary anytime. The portal is mobile-friendly and includes real-time park information.
          </p>
        </div>
      </BaseModal.Body>

      <BaseModal.Footer>
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-200 transition-colors hover:bg-gray-50"
          type="button"
        >
          Close
        </button>
      </BaseModal.Footer>
    </BaseModal>
  );
};

export default ShareClientModal;
